import functions from "@/functions";

const sortUniqueItems = (itemList: UnknownObj[]): UnknownObj[] => {
  return functions.getUniques(itemList || []).sort((a, b): number => {
    const nameA = a?.text || "";
    const nameB = b?.text || "";
    if (nameA > nameB) return 1;
    if (nameA < nameB) return -1;
    return 0;
  });
};

const mutations = {
  UPDATE(state: StateObj, { field = "none", value = null }) {
    state[field] = value;
  },
  RESET(state: CampaignStateObj) {
    state.campaigns = [];
    state.list = {} as CampaignsList;
    state.suggestedProducts = [];
    state.availableProducts = [];
    state.selectedProductIds = [];
    state.selectedFormats = [];
    state.active = {};
  },
  RESET_CAMPAIGN(state: CampaignStateObj) {
    state.suggestedProducts = [];
    state.availableProducts = [];
    state.selectedProductIds = [];
    state.selectedFormats = [];
    state.active = {};
  },
  RESET_PRODUCTS(state: CampaignStateObj) {
    state.suggestedProducts = [];
    state.availableProducts = [];
    state.selectedProductIds = [];
    state.selectedFormats = [];
    state.active.products = [];
  },
  LIST_UPDATE(state: CampaignStateObj, campaigns: Campaign[]) {
    const campaingsList: UnknownObj = {};
    state.list = {} as CampaignsList;
    state.campaigns = [];

    state.statusList.forEach((status: string) => {
      campaingsList[status] = functions.campaingsByStatus(campaigns, status);
    });

    state.list = campaingsList as CampaignsList;

    state.campaigns = campaigns;
  },
  LIST_ADD_ITEM(state: CampaignStateObj, campaign: Campaign) {
    const status: string = campaign.status || "draft";
    const updatedList: Campaign[] = state.list[status] || [];

    state.list[status] = [campaign, ...updatedList];
  },
  UPDATE_MARKET_SEGMENTS(state: CampaignStateObj, segments: MarketSegment[]) {
    state.marketSegmentList = (segments || []).map((item: MarketSegment) => {
      return {
        value: item.id,
        text: item.name
      } as MarketSegment;
    });
  },
  UPDATE_LOCATION_RESULTS(state: CampaignStateObj, locations: LocationItem[]) {
    state.locationSearchResults = sortUniqueItems([...locations] || []).map(
      location => {
        location.broadcasterId =
          location.broadcasterId || location?.broadcaster?.id;
        return location;
      }
    );
  },
  UPDATE_LOCATIONS(state: CampaignStateObj, payload: UnknownObj) {
    const { locations = [], broadcasterName, update = false } = payload;
    const locationsList = update ? state.locationsList : [];

    state.locationsList = sortUniqueItems([...locationsList, ...locations]);

    const locationsBroadcasterNames = update
      ? state.locationsBroadcasterNames
      : ([] as string[]);
    if (!locationsBroadcasterNames.includes(broadcasterName)) {
      locationsBroadcasterNames.push(broadcasterName);
      state.locationsBroadcasterNames = locationsBroadcasterNames;
    }
  },
  UPDATE_PRODUCT(state: CampaignStateObj, updatedProduct: Product) {
    state.suggestedProducts = [...(state.suggestedProducts || [])].map(
      (product: Product) => {
        if (product.id === updatedProduct.id) {
          return updatedProduct;
        }

        return product;
      }
    );

    const selectedFormats = [...state.selectedFormats];
    state.selectedFormats = selectedFormats.map(format => {
      const format2 =
        selectedFormats.filter(form => form.id === format.id)[0] || {};
      return { ...format, ...format2 };
    });

    const activeCampaign = { ...state.active };

    state.active = {
      ...activeCampaign,
      products: [...state.suggestedProducts]
    };
  },
  SELECTED_FORMATS(state: CampaignStateObj, selectedFormats: ProductFormat[]) {
    state.selectedFormats = [...selectedFormats];

    state.suggestedProducts = [...state.suggestedProducts].map(
      (product: Product) => {
        let total = 0;
        product.formats = (product.formats || []).map(
          (format: ProductFormat) => {
            const format2 =
              selectedFormats.filter(form => form.id === format.id)[0] || {};
            if (format2?.amount && format2?.unitPrice) {
              total += format2.amount * format2.unitPrice;
            }

            return { ...format, ...format2 };
          }
        );

        return { ...product, total };
      }
    );
  },
  SELECTED_PRODUCTS_ADD(state: CampaignStateObj, productId: number) {
    state.ignoredProductIds = state.ignoredProductIds.filter(
      (item: number) => item !== productId
    );

    if (!state.selectedProductIds.includes(productId)) {
      state.selectedProductIds.push(productId);
    }

    const selectedFormats = state.selectedFormats;

    state.suggestedProducts = [...state.suggestedProducts].map(
      (product: Product) => {
        let total = 0;
        product.formats = (product.formats || []).map(
          (format: ProductFormat) => {
            const format2 =
              selectedFormats.filter(form => form.id === format.id)[0] || {};
            if (format2?.amount && format2?.unitPrice) {
              total += format2.amount * format2.unitPrice;
            }

            return { ...format, ...format2 };
          }
        );

        return { ...product, total };
      }
    );
  },
  SELECTED_PRODUCTS_REMOVE(state: CampaignStateObj, productId: number) {
    state.selectedProductIds = state.selectedProductIds.filter(
      (item: number) => item !== productId
    );

    if (!state.ignoredProductIds.includes(productId)) {
      state.ignoredProductIds.push(productId);
    }
  },
  PRODUCTS_LIST(state: CampaignStateObj, availableProducts: Product[]) {
    state.availableProducts = availableProducts;
  },
  PRODUCTS_LIST_MERGE(state: CampaignStateObj, campaignProducts: Product[]) {
    const selectedProductIds: number[] = [];
    const selectedProductsDictionary: UnknownObj = {};

    [...campaignProducts, ...(state.active?.products || [])].forEach(
      product => {
        const productId = product.id || 0;
        selectedProductIds.push(productId);
        selectedProductsDictionary[productId] = product;
      }
    );

    state.selectedProductIds = selectedProductIds;

    state.availableProducts
      .filter(product => selectedProductIds.includes(product?.id || 0))
      .forEach(product => {
        const productId = product?.id || 0;
        const productToUpdate = selectedProductsDictionary[productId];

        productToUpdate.formats = sortUniqueItems([
          ...(productToUpdate.formats || []),
          ...(product?.formats || [])
        ]);

        selectedProductsDictionary[productId] = productToUpdate;
      });

    campaignProducts = Object.values(selectedProductsDictionary);

    state.suggestedProducts = sortUniqueItems(campaignProducts);

    const activeCampaign = { ...state.active };

    state.active = {
      ...activeCampaign,
      products: [...state.suggestedProducts]
    };
  },
  PRODUCTS_LIST_UPDATE(state: CampaignStateObj, productListId: number[]) {
    if (!productListId) {
      productListId = state.selectedProductIds;
    } else {
      productListId.forEach(productId => {
        if (!state.selectedProductIds.includes(productId)) {
          state.selectedProductIds.push(productId);
        }
      });
    }

    const selectedFormats = state.selectedFormats;

    const selectedProductIds = state.selectedProductIds;
    const defaultProductsDictionary: UnknownObj = {};

    state.availableProducts
      .filter(product => selectedProductIds.includes(product?.id || 0))
      .forEach(product => {
        const productId = product?.id || 0;
        defaultProductsDictionary[productId] = product;
      });

    state.suggestedProducts = sortUniqueItems(
      [...state.availableProducts, ...state.suggestedProducts].filter(
        (product: Product) => {
          const productId = product.id || 0;
          return productListId.includes(productId);
        }
      )
    ).map((product: Product) => {
      const productId = product?.id || 0;
      let productTotal = 0.0;

      const defaultProductFormats =
        defaultProductsDictionary[productId]?.formats || [];

      product.formats = sortUniqueItems([
        ...(product.formats || []),
        ...defaultProductFormats
      ]).map((format: ProductFormat) => {
        const foundFormat =
          selectedFormats.filter(form => form.id === format.id)[0] || {};

        format = { ...format, ...foundFormat };

        productTotal += (format?.amount || 0) * (format?.unitPrice || 0);

        return format;
      });

      product.total = productTotal;

      return product;
    });

    const activeCampaign = { ...state.active };

    state.active = {
      ...activeCampaign,
      products: [...state.suggestedProducts]
    };
  },
  PRODUCTS_SUGGESTION(state: CampaignStateObj, payload: UnknownObj) {
    const suggestedProducts: Product[] = (payload?.data || []).filter(
      (product: Product) => !!product.total
    );

    const { reset } = payload;

    if (!reset) {
      state.suggestedProducts = suggestedProducts;
    } else {
      state.suggestedProducts = suggestedProducts.map(item => {
        item.formats = [...(item?.formats || [])]?.map(format => {
          format.amount = 0;
          return format;
        });

        return item;
      });
    }

    const selectedProductIds: number[] = [];
    suggestedProducts.forEach(product => {
      const productId = product.id || 0;
      selectedProductIds.push(productId);
    });

    state.selectedProductIds = selectedProductIds;

    state.availableProducts = (payload.data || []) as Product[];
    state.productsCount = state.availableProducts.length || 0;

    const activeCampaign = { ...state.active };

    state.active = {
      ...activeCampaign,
      products: [...state.suggestedProducts]
    };
  },
  UPDATE_INVESTMENT(state: CampaignStateObj, minPrice: number) {
    state.minInvestment = minPrice;
  },
  UPDATE_BROADCASTERS(
    state: CampaignStateObj,
    broadcasters: BroadcastChannel[]
  ) {
    state.broadcasters = broadcasters;
  },
  SET_ACTIVE(state: CampaignStateObj, campaign: Campaign) {
    state.active = campaign;
  }
};

export default mutations;
